import {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
  useReducer,
} from "react";

import { Teacher } from "../../models/teacher/teacher";
import { updatePartialObjectReducer } from "../../helpers/state-helpers/update-partial-object-reducer";

export type GlobalState = {
  isLoading: boolean;
  teachers: Teacher[];
};

const getGlobalStateDefaults = (): GlobalState => ({
  isLoading: false,
  teachers: [],
});

const GlobalStateContext = createContext<GlobalState>(getGlobalStateDefaults());
const GlobalStateDispatchContext = createContext<
  Dispatch<Partial<GlobalState>> | undefined
>(undefined);

export function GlobalStateProvider({ children }: { children: ReactNode }) {
  const [globalState, dispatch] = useReducer(
    updatePartialObjectReducer<GlobalState>,
    getGlobalStateDefaults(),
  );

  return (
    <GlobalStateContext.Provider value={globalState}>
      <GlobalStateDispatchContext.Provider value={dispatch}>
        {children}
      </GlobalStateDispatchContext.Provider>
    </GlobalStateContext.Provider>
  );
}

export function useGlobalState() {
  const globalState = useContext(GlobalStateContext);
  const dispatch = useContext(GlobalStateDispatchContext);

  if (!dispatch) {
    throw Error("Dispatch cannot be undefined");
  }

  const setIsLoading = useCallback(
    (value: boolean) => dispatch({ isLoading: value }),
    [dispatch],
  );

  return {
    globalState,
    setGlobalState: dispatch,
    setIsLoading,
  };
}
